import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PermissionGuard } from '@components/PermissionGuard';
import { SalesGoalUpsertPayload } from '../../types/salesGoal';
import salesGoalService from '../../services/salesGoalService';
import { ChevronLeft, ChevronRight, Check, Loader2, Target } from 'lucide-react';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

interface CellState {
  /** Valor em centavos como string de dígitos ('' = vazio / sem meta) */
  cents: string;
  dirty: boolean;
  saving: boolean;
}

const emptyRow = (): CellState => ({ cents: '', dirty: false, saving: false });

/** Formata uma string de centavos (dígitos) como moeda brasileira "R$ 12.003,88". */
const formatCents = (digits: string): string => {
  if (digits === '') return '';
  const reais = parseInt(digits, 10) / 100;
  return reais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/** Converte um valor em Reais (float) para string de centavos (dígitos). */
const reaisToCents = (value: number | null | undefined): string => {
  if (value == null) return '';
  return String(Math.round(value * 100));
};

export const SalesGoalsView: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState<CellState[]>(Array(12).fill(null).map(emptyRow));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load sales goals when year changes
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await salesGoalService.getByYear(year);
        const newRows: CellState[] = Array(12).fill(null).map((_, idx) => {
          const refDate = `${year}-${String(idx + 1).padStart(2, '0')}-01`;
          const found = data.find(d => {
            // Normalize to first 10 chars to handle possible ISO datetime strings
            const normalized = d.reference_date ? String(d.reference_date).substring(0, 10) : '';
            return normalized === refDate;
          });
          return {
            cents: found?.value != null ? reaisToCents(found.value) : '',
            dirty: false,
            saving: false,
          };
        });
        setRows(newRows);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year]);

  const buildReferenceDate = (monthIndex: number) =>
    `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;

  const handleCellChange = (monthIdx: number, value: string) => {
    // Keep only digits — the input behaves as a cents mask.
    const digits = value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    setRows(prev => prev.map((row, i) => (i === monthIdx ? { ...row, cents: digits, dirty: true } : row)));
  };

  const showSuccess = (monthIdx: number) => {
    setSuccessMsg(`${MONTHS[monthIdx]} salvo com sucesso`);
    setTimeout(() => setSuccessMsg(null), 2000);
  };

  const saveCell = useCallback(async (monthIdx: number) => {
    const row = rows[monthIdx];
    if (!row.dirty) return;

    const referenceDate = buildReferenceDate(monthIdx);

    // Empty cell => delete the record
    if (row.cents.trim() === '') {
      setRows(prev => prev.map((r, i) => (i === monthIdx ? { ...r, saving: true } : r)));
      try {
        await salesGoalService.delete(referenceDate);
        setRows(prev => prev.map((r, i) => (i === monthIdx ? { ...r, saving: false, dirty: false } : r)));
        showSuccess(monthIdx);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao salvar');
        setRows(prev => prev.map((r, i) => (i === monthIdx ? { ...r, saving: false } : r)));
      }
      return;
    }

    const payload: SalesGoalUpsertPayload = {
      reference_date: referenceDate,
      value: parseInt(row.cents, 10) / 100,
    };

    setRows(prev => prev.map((r, i) => (i === monthIdx ? { ...r, saving: true } : r)));
    try {
      await salesGoalService.upsert(payload);
      setRows(prev => prev.map((r, i) => (i === monthIdx ? { ...r, saving: false, dirty: false } : r)));
      showSuccess(monthIdx);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
      setRows(prev => prev.map((r, i) => (i === monthIdx ? { ...r, saving: false } : r)));
    }
  }, [rows, year]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, monthIdx: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveCell(monthIdx);
      const next = inputRefs.current[monthIdx + 1];
      if (next) next.focus();
    } else if (e.key === 'Tab') {
      saveCell(monthIdx);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = inputRefs.current[monthIdx + 1];
      if (next) next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = inputRefs.current[monthIdx - 1];
      if (prev) prev.focus();
    }
  };

  return (
    <PermissionGuard permission="sales_goals.read" showError>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Meta de Venda</h1>
          <p className="text-sm text-slate-500 mt-1">
            Defina a meta de vendas mensal consolidada da Viver Organizer (valores em Reais).
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-700">
            <Target size={18} className="text-emerald-600" />
            <span className="text-sm font-medium">Meta consolidada</span>
          </div>

          {/* Year selector */}
          <div className="flex items-center gap-2">
            <button
              className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
              onClick={() => setYear(y => y - 1)}
              title="Ano anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-lg font-semibold text-slate-800 min-w-[60px] text-center">
              {year}
            </span>
            <button
              className="p-1.5 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={() => setYear(y => y + 1)}
              disabled={year >= currentYear + 1}
              title="Próximo ano"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2">
            <Check size={16} />
            {successMsg}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500">Carregando...</span>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 w-48">Mês</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-700">Meta de Venda (R$)</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((month, idx) => {
                  const row = rows[idx];
                  return (
                    <tr
                      key={idx}
                      className={`border-b border-slate-100 ${row?.dirty ? 'bg-amber-50/50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-4 py-2 font-medium text-slate-700">
                        {month}/{year}
                      </td>
                      <td className="px-4 py-1">
                        <input
                          ref={el => { inputRefs.current[idx] = el; }}
                          type="text"
                          inputMode="numeric"
                          className="w-full text-right border border-slate-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="R$ 0,00"
                          value={formatCents(row?.cents ?? '')}
                          onChange={e => handleCellChange(idx, e.target.value)}
                          onKeyDown={e => handleKeyDown(e, idx)}
                          onBlur={() => saveCell(idx)}
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        {row?.saving && <Loader2 size={14} className="animate-spin text-emerald-500 inline" />}
                        {!row?.saving && row?.dirty && (
                          <span className="text-amber-500 text-xs" title="Pressione Enter para salvar">●</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
              Digite apenas os números — o valor é formatado automaticamente em Reais. Pressione{' '}
              <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Enter</kbd> ou{' '}
              <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Tab</kbd> para salvar. Limpe a célula para remover a meta do mês.
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};
